import React, { useContext } from "react"
import { MobergButton, MobergButtonVariant, MobergColumn, MobergDropdown, MobergFontSize, MobergHeader, MobergInputLabel, MobergRow, MobergText, MobergTheme, getColors } from "../../../../../Moberg"
import { ConfigureWindowModalContext } from "../ConfigureWindowModal"
import { RenderStrategy, TraceConfigJSON } from "../../../../../Pages/Data/Visualize/DataReview/Types/Trace"
import { TraceGoal } from "../../../../../Pages/Data/Visualize/DataReview/Types/AnalysisDetails"
import { BIN_SIZE_OPTIONS, BIN_START_OPTIONS, binStartText, canHaveGoal, goalError, goalLines, goalText, newTraceGoal, rangeOf, vitalForModality } from "../../../../../Pages/Data/Visualize/DataReview/Types/GoalRanges"
import { DirectionPicker, GoalLinesPicture, GoalOptionCard, GoalSwitch, GoalValues, Hint, ShadingPicture, TimeInGoalBarPicture } from "./GoalControls"

/* Goals for the measurements in a line graph: each measurement can have a goal, read one way
   (below, between or above), and each way of showing it is switched on or off on its own. */
export const GoalSection: React.FC = () => {
	const { currentGraph, updateGraphProperty } = useContext(ConfigureWindowModalContext) as any
	const measurements = ((currentGraph?.traces ?? []) as any[]).filter(trace =>
		canHaveGoal(trace) && (trace.renderStrategy === RenderStrategy.LINE || trace.renderStrategy === RenderStrategy.LINE_STEP)
	)

	const setGoal = (trace: any, changes: Partial<TraceGoal>) => {
		const goal: TraceGoal = { ...(trace.goal ?? newTraceGoal(trace.rawDataKey)), ...changes }
		updateGraphProperty("traces", (previous: TraceConfigJSON[]) => previous.map(other => other.id === trace.id ? { ...other, goal } : other))
	}

	return (
		<MobergColumn gap="16px">
			<MobergHeader>Goals</MobergHeader>
			<Hint>Set when a measurement is in goal, then choose how the goal shows.</Hint>

			{measurements.length === 0 && (
				<Hint>Add a measurement under Source data first.</Hint>
			)}

			{measurements.map(trace => <MeasurementGoal key={trace.id} trace={trace} onChange={changes => setGoal(trace, changes)} />)}
		</MobergColumn>
	)
}

const MeasurementGoal: React.FC<{ trace: any, onChange: (changes: Partial<TraceGoal>) => void }> = ({ trace, onChange }) => {
	const goal: TraceGoal = trace.goal ?? newTraceGoal(trace.rawDataKey)
	const vital = vitalForModality(trace.rawDataKey)
	const units = vital?.units ?? trace.units ?? ""
	const error = goal.enabled ? goalError(goal) : undefined
	const usual = vital && goalText(vital.goal)
	const usualDiffers = vital && (goal.lo !== vital.goal.lo || goal.hi !== vital.goal.hi)
	const red = getColors(MobergTheme.RED).main
	const name = vital?.label ?? trace.name
	const range = rangeOf(goal)
	const lineValues = goalLines(range).map(line => line.value)
	const summary = goal.direction === "below" ? `${name} below ${range.hi} ${units}` : goal.direction === "above" ? `${name} above ${range.lo} ${units}` : `${name} between ${range.lo} and ${range.hi} ${units}`
	const outOfGoal = goal.direction === "below" ? `at or above ${range.hi}` : goal.direction === "above" ? `at or below ${range.lo}` : `outside ${range.lo} to ${range.hi}`

	return (
		<MobergColumn gap="20px" style={{ maxWidth: "820px", padding: "16px 20px 20px", border: "1px solid var(--color-border-default)", borderRadius: "10px", background: "var(--color-surface-panel)" }}>
			<MobergRow gap="12px" wrap style={{ paddingBottom: goal.enabled ? "16px" : 0, borderBottom: goal.enabled ? "1px solid var(--color-border-light)" : "none" }}>
				<MobergColumn gap="2px">
					<MobergText fontSize={MobergFontSize.X_LARGE} fontWeight="bold">{trace.name} goal</MobergText>
					<Hint>{!goal.enabled ? "Turn it on to mark time out of goal." : error ? "Set the goal values below." : `In goal when ${summary.trim()}`}</Hint>
				</MobergColumn>
				<div style={{ flex: 1 }} />
				{goal.enabled && usual && usualDiffers && (
					<>
						<Hint>Usual for {vital!.label}: {usual} {vital!.units}</Hint>
						<MobergButton
							variant={MobergButtonVariant.OUTLINE}
							theme={MobergTheme.BLUE}
							onClick={() => onChange({ ...newTraceGoal(trace.rawDataKey, true), lines: goal.lines, heatmap: goal.heatmap, shading: goal.shading, binMs: goal.binMs, binStart: goal.binStart })}
						>
							Use
						</MobergButton>
					</>
				)}
				<GoalSwitch label={`${trace.name} goal`} checked={goal.enabled} onChange={enabled => onChange({ enabled })} />
			</MobergRow>

			{goal.enabled && (
				<>
					<MobergColumn gap="10px">
						<MobergInputLabel text="In goal when" />
						<MobergRow gap="16px" wrap>
							<DirectionPicker value={goal.direction} onChange={direction => onChange({ direction })} />
							<GoalValues id={trace.id} direction={goal.direction} lo={goal.lo} hi={goal.hi} units={units} onChange={onChange} />
						</MobergRow>
						{error && <MobergText style={{ color: red }}>{error}</MobergText>}
					</MobergColumn>

					<MobergColumn gap="10px">
						<MobergInputLabel text="Show on the graph" />
						<div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "12px" }}>
							<GoalOptionCard
								label="Goal lines"
								description={error ? "Dashed lines at the goal values" : `Dashed ${lineValues.length > 1 ? "lines" : "line"} at ${lineValues.join(" and ")} ${units}`.trim()}
								picture={<GoalLinesPicture />}
								checked={goal.lines}
								onChange={lines => onChange({ lines })}
							/>
							<GoalOptionCard
								label="Shade out of goal"
								description={error ? `Fills where ${name} is out of goal` : `Fills where ${name} is ${outOfGoal} ${units}`.trim()}
								picture={<ShadingPicture />}
								checked={goal.shading}
								onChange={shading => onChange({ shading })}
							/>
							<GoalOptionCard
								label="Time in goal bar"
								description={`A bar under the graph, green when ${name} stayed in goal and red when it did not`}
								picture={<TimeInGoalBarPicture />}
								checked={goal.heatmap}
								onChange={heatmap => onChange({ heatmap, barHidden: false })}
							>
								{/* Always shown, so every card keeps its height; greyed out while the bar is off. */}
								<div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "4px" }}>
									<Hint style={{ fontSize: "13px" }}>Each block covers</Hint>
									<MobergDropdown width="100%" disabled={!goal.heatmap} selectedValue={goal.binMs} options={BIN_SIZE_OPTIONS} onChange={(binMs: number) => onChange({ binMs })} />
									<Hint style={{ fontSize: "13px", marginTop: "6px" }}>Count blocks from</Hint>
									<MobergDropdown width="100%" disabled={!goal.heatmap} selectedValue={goal.binStart ?? 0} options={BIN_START_OPTIONS} onChange={(binStart: number | "recording") => onChange({ binStart })} />
									<Hint style={{ fontSize: "12px", opacity: goal.heatmap ? 1 : 0.7 }}>{binStartText(goal.binMs, goal.binStart)}</Hint>
								</div>
							</GoalOptionCard>
						</div>
					</MobergColumn>
				</>
			)}
		</MobergColumn>
	)
}
